import { useRef, type ReactNode } from 'react';
import { motion, useMotionValue, useReducedMotion, useTransform } from 'motion/react';
import { Check, SkipForward } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SwipeHint {
  icon: ReactNode;
  label: string;
  /** Classes for the revealed zone (bg + text color). */
  className: string;
}

/**
 * The house swipe vocabulary — one definition so every swipeable list (Today
 * up-next rows, Inbox rows) reveals identical hints. Right = commit (done /
 * log), left = skip.
 */
export const SWIPE_HINTS = {
  done: {
    icon: <Check className="h-4 w-4" aria-hidden />,
    label: 'Done',
    className: 'bg-ok-bg text-ok-ink',
  },
  log: {
    icon: <Check className="h-4 w-4" aria-hidden />,
    label: 'Log',
    className: 'bg-ok-bg text-ok-ink',
  },
  skip: {
    icon: <SkipForward className="h-4 w-4" aria-hidden />,
    label: 'Skip',
    className: 'bg-warn-bg text-t1',
  },
} satisfies Record<string, SwipeHint>;

export interface SwipeableRowProps {
  children: ReactNode;
  /** Fires when the row is swiped right past the threshold (e.g. complete). */
  onSwipeRight?: (() => void) | undefined;
  /** Fires when the row is swiped left past the threshold (e.g. skip). */
  onSwipeLeft?: (() => void) | undefined;
  rightHint?: SwipeHint;
  leftHint?: SwipeHint;
  className?: string;
}

const TRIGGER_PX = 64;
const MAX_DRAG_PX = 104;

/**
 * Horizontal swipe wrapper for agenda rows: swipe right to complete/log,
 * swipe left to skip. The row snaps back on release (the action itself
 * removes the row and shows an undo toast, so no committed-state animation
 * is needed). Tens-of-times-a-day interaction — the only motion is the
 * default snap-back spring, no added flourish.
 *
 * Taps still work: a drag past the pointer threshold suppresses the click
 * that fires on release. Under prefers-reduced-motion the row renders
 * static — every swipe action remains reachable via the row's buttons.
 */
export function SwipeableRow({
  children,
  onSwipeRight,
  onSwipeLeft,
  rightHint,
  leftHint,
  className,
}: SwipeableRowProps) {
  const reduced = useReducedMotion();
  const x = useMotionValue(0);
  const rightOpacity = useTransform(x, [8, TRIGGER_PX * 0.75], [0, 1]);
  const leftOpacity = useTransform(x, [-TRIGGER_PX * 0.75, -8], [1, 0]);
  const dragging = useRef(false);

  if (reduced || (!onSwipeRight && !onSwipeLeft)) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={cn('relative overflow-hidden rounded-xl', className)}
      onClickCapture={(e) => {
        if (dragging.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {rightHint && (
        <motion.div
          aria-hidden
          style={{ opacity: rightOpacity }}
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 flex w-1/2 items-center justify-start gap-1.5 rounded-l-xl ps-4 text-caption font-medium',
            rightHint.className,
          )}
        >
          {rightHint.icon}
          {rightHint.label}
        </motion.div>
      )}
      {leftHint && (
        <motion.div
          aria-hidden
          style={{ opacity: leftOpacity }}
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 flex w-1/2 items-center justify-end gap-1.5 rounded-r-xl pe-4 text-caption font-medium',
            leftHint.className,
          )}
        >
          {leftHint.icon}
          {leftHint.label}
        </motion.div>
      )}
      {/* relative: with transform:none at rest, a plain div would paint BELOW
          the positioned hint overlays and they'd swallow every tap. */}
      <motion.div
        className="relative"
        style={{ x }}
        drag="x"
        dragSnapToOrigin
        dragElastic={0.12}
        dragConstraints={{
          left: onSwipeLeft ? -MAX_DRAG_PX : 0,
          right: onSwipeRight ? MAX_DRAG_PX : 0,
        }}
        onDragStart={() => {
          dragging.current = true;
        }}
        onDragEnd={(_e, info) => {
          if (info.offset.x >= TRIGGER_PX && onSwipeRight) onSwipeRight();
          else if (info.offset.x <= -TRIGGER_PX && onSwipeLeft) onSwipeLeft();
          // Release the click guard after the release-click has fired.
          setTimeout(() => {
            dragging.current = false;
          }, 50);
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}
